import { getRuntimeEnv } from '@explorer/helpers/env';
import { parseJWT } from '@explorer/helpers/parseJWT';

/* Must match the redirect URI registered on the Hydra client. */
export const CENTRALIZED_LOGIN_CALLBACK_PATH = '/login/callback';

const getHydraRedirectUri = (): string => {
  return `${getRuntimeEnv('APP_DOMAIN').replace(
    /\/$/,
    '',
  )}${CENTRALIZED_LOGIN_CALLBACK_PATH}`;
};

const PKCE_VERIFIER_STORAGE_KEY = 'pkce_code_verifier';
const CENTRALIZED_ID_TOKEN_STORAGE_KEY = 'centralized_login_id_token';
const AUTHORIZE_SCOPE = 'openid profile email offline_access';
const RANDOM_BYTE_COUNT = 32;
const MAX_TIMEOUT = 2_147_483_647;
const TOKEN_EXCHANGE_TIMEOUT_MS = 30_000;

export interface CentralizedLoginTokens {
  access_token: string;
  id_token: string | null;
  redirect?: string;
}

let inFlight = false;

const base64UrlEncode = (bytes: Uint8Array): string => {
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join(
    '',
  );

  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const encodeState = (redirect: string): string => {
  return base64UrlEncode(
    new TextEncoder().encode(JSON.stringify({ redirect })),
  );
};

export const decodeStateRedirect = (state?: string): string | undefined => {
  if (!state) {
    return undefined;
  }

  try {
    const base64 = state.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    const bytes = Uint8Array.from(window.atob(paddedBase64), (character) =>
      character.charCodeAt(0),
    );
    const payload = JSON.parse(new TextDecoder().decode(bytes)) as {
      redirect?: unknown;
    };

    return typeof payload.redirect === 'string' &&
      isSafeRedirectPath(payload.redirect)
      ? payload.redirect
      : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Reads the authorization params straight off the URL. `router.query` is still
 * empty while the router hydrates, and acting on that empty query consumes the
 * single-use PKCE verifier before the code is visible, burning the login.
 */
export const getCallbackParams = (): { code?: string; state?: string } => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);

  return {
    code: params.get('code') ?? undefined,
    state: params.get('state') ?? undefined,
  };
};

/**
 * Trades an authorization code for a session. The request is bounded by an
 * abort timeout so a blackholed API cannot leave the callback loading forever.
 */
export const exchangeAuthorizationCode = async (
  code: string,
  codeVerifier: string,
  state?: string,
): Promise<CentralizedLoginTokens> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    TOKEN_EXCHANGE_TIMEOUT_MS,
  );

  try {
    const response = await fetch(
      new URL('/auth/login', getRuntimeEnv('API_PORTAL')),
      {
        method: 'POST',
        headers: {
          'Coalition-App': 'coalition-explorer',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          code_verifier: codeVerifier,
          state: state ?? '',
        }),
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      throw new Error(`Login exchange failed with status ${response.status}`);
    }

    return (await response.json()) as CentralizedLoginTokens;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const consumePkceVerifier = (): string | null => {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_STORAGE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_STORAGE_KEY);
  return verifier;
};

export const storeCentralizedSession = (
  accessToken: string,
  idToken: string,
): void => {
  localStorage.setItem('bearer', accessToken);
  localStorage.setItem(CENTRALIZED_ID_TOKEN_STORAGE_KEY, idToken);
};

/**
 * Drops the stored tokens but leaves the PKCE verifier alone, so reading a
 * missing session cannot destroy an authorization that is still in flight.
 */
export const clearCentralizedTokens = (): void => {
  localStorage.removeItem('bearer');
  localStorage.removeItem(CENTRALIZED_ID_TOKEN_STORAGE_KEY);
};

export const clearCentralizedSession = (): void => {
  clearCentralizedTokens();
  sessionStorage.removeItem(PKCE_VERIFIER_STORAGE_KEY);
};

export const getStoredCentralizedSession = ():
  | { access_token: string; id_token: string; expires_at: number }
  | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const accessToken = localStorage.getItem('bearer');
  const idToken = localStorage.getItem(CENTRALIZED_ID_TOKEN_STORAGE_KEY);

  /* No id token means this is not a centralized session. The bearer may belong
   * to Auth0, so it must be left alone or every API query stays disabled. */
  if (!idToken) {
    return undefined;
  }

  if (!accessToken) {
    clearCentralizedTokens();
    return undefined;
  }

  const accessTokenPayload = parseJWT<{ exp?: unknown }>(accessToken);
  const idTokenPayload = parseJWT<{ exp?: unknown }>(idToken);
  const expirations = [accessTokenPayload?.exp, idTokenPayload?.exp];

  if (
    expirations.some(
      (expiration) =>
        typeof expiration !== 'number' || expiration * 1000 <= Date.now(),
    )
  ) {
    clearCentralizedTokens();
    return undefined;
  }

  return {
    access_token: accessToken,
    id_token: idToken,
    expires_at: Math.min(...(expirations as number[])) * 1000,
  };
};

export const getCentralizedSessionExpiryDelay = (expiresAt: number): number => {
  return Math.min(Math.max(expiresAt - Date.now(), 0), MAX_TIMEOUT);
};

export const isSafeRedirectPath = (redirect?: string): redirect is string => {
  return (
    !!redirect &&
    redirect.startsWith('/') &&
    !redirect.startsWith('//') &&
    /* Returning to the callback without a code would fail the exchange again. */
    !redirect.startsWith(CENTRALIZED_LOGIN_CALLBACK_PATH)
  );
};

export const getSafeRedirectPath = (url: URL): string | undefined => {
  url.searchParams.delete('code');
  url.searchParams.delete('state');

  if (url.pathname === '/login') {
    const requestedRedirect = url.searchParams.get('redirect') ?? undefined;
    return isSafeRedirectPath(requestedRedirect)
      ? requestedRedirect
      : undefined;
  }

  const redirect = `${url.pathname}${url.search}`;

  return isSafeRedirectPath(redirect) ? redirect : undefined;
};

export const buildCentralizedLoginUrl = async (
  redirect?: string,
): Promise<string> => {
  const verifierBytes = crypto.getRandomValues(
    new Uint8Array(RANDOM_BYTE_COUNT),
  );
  const verifier = base64UrlEncode(verifierBytes);
  const challengeBytes = new Uint8Array(
    await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(verifier),
    ),
  );

  sessionStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, verifier);

  const authorizeUrl = new URL(
    '/v1/oauth/authorize',
    getRuntimeEnv('AUTH_SERVICE_URL'),
  );
  authorizeUrl.searchParams.set(
    'client_id',
    getRuntimeEnv('HYDRA_CLIENT_ID'),
  );
  authorizeUrl.searchParams.set('redirect_uri', getHydraRedirectUri());
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('scope', AUTHORIZE_SCOPE);
  authorizeUrl.searchParams.set('code_challenge', base64UrlEncode(challengeBytes));
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');

  if (isSafeRedirectPath(redirect)) {
    authorizeUrl.searchParams.set('state', encodeState(redirect));
  }

  return authorizeUrl.toString();
};

/**
 * Builds the Coalition Auth Service (Hydra) logout URL, which ends the upstream
 * session before the user is sent back to the app.
 *
 * @param postLogoutRedirectUri - where Hydra returns the user; defaults to the app homepage.
 * @returns the logout URL, or null when the auth service is not configured.
 */
export const buildHydraLogoutUrl = (
  postLogoutRedirectUri: string = getRuntimeEnv('APP_DOMAIN'),
): string | null => {
  const authServiceUrl = getRuntimeEnv('AUTH_SERVICE_URL');
  const clientId = getRuntimeEnv('HYDRA_CLIENT_ID');

  if (!authServiceUrl || !clientId) {
    return null;
  }

  const logoutUrl = new URL(
    `${authServiceUrl.replace(/\/$/, '')}/v1/oauth/logout`,
  );
  logoutUrl.searchParams.set('client_id', clientId);
  logoutUrl.searchParams.set(
    'post_logout_redirect_uri',
    postLogoutRedirectUri,
  );

  return logoutUrl.toString();
};

export const redirectToCentralizedLogout = (): void => {
  clearCentralizedSession();
  /* Clearing the session re-runs the login effect, and a second navigation
   * would cancel this one and silently re-authenticate against the upstream
   * session this request is meant to end. Hold the guard until the unload. */
  inFlight = true;

  window.location.href =
    buildHydraLogoutUrl() || getRuntimeEnv('APP_DOMAIN') || '/';
};

export const redirectToCentralizedLogin = async (
  currentUrl: string,
): Promise<void> => {
  if (inFlight) {
    return;
  }

  inFlight = true;

  try {
    const url = new URL(currentUrl);
    const authorizeUrl = await buildCentralizedLoginUrl(
      getSafeRedirectPath(url),
    );

    window.location.href = authorizeUrl;
  } catch (error) {
    inFlight = false;
    throw error;
  }
};

/**
 * A 401 is the only client-side signal that an otherwise live token was revoked.
 * Clear it before authorizing again so AuthStore cannot restore the stale session.
 */
export const recoverCentralizedSession = (): void => {
  if (
    typeof window === 'undefined' ||
    !localStorage.getItem(CENTRALIZED_ID_TOKEN_STORAGE_KEY)
  ) {
    return;
  }

  clearCentralizedSession();
  void redirectToCentralizedLogin(window.location.href);
};
